"use client"

import { useEffect } from "react"
import { setBottomDockContribution } from "@/hooks/use-bottom-dock-inset"

// Mediavine Journey pins ad units to the viewport bottom (anchor/adhesion and
// the Universal Player) entirely outside our React tree, so no first-party dock
// or FAB knows they exist. This component measures the band they occupy and
// feeds its height into the bottom-dock system so the chat FAB, sticky CTAs,
// and result save bar all clear it automatically.
//
// MEASURE THE CREATIVE, NOT A "BAR" ELEMENT. The original probe looked for a
// body-level element that was itself `position: fixed` and 30-160px tall. That
// stopped matching anything: Journey now nests every bottom unit inside one
// shared shell, and neither the shell nor the units fit that shape —
//
//   fixed_container_bottom    position:fixed, ~400px tall, body child
//     universalPlayer_wrapper position:relative
//       universalPlayer       position:static
//
// The shell is rejected on height, the units on position, and the units sit
// three levels down besides. Measured 0 against production on every route,
// desktop and mobile, so the dock coordination shipped in PR #130 was a no-op
// and the player was overlapping the seeding-calculator's sticky result bar.
//
// Detection stays structural rather than selector-based, because the wrapper
// markup is undocumented and evidently does change. What is stable is the ad
// pixels themselves: a rendered creative is an iframe or a <video>, inside a
// fixed-position ancestor. Our own bottom bars never contain iframes or videos,
// which keeps the probe from ever counting first-party UI.
//
// The Universal Player needs both element kinds. Its own adunit iframe stays
// `visibility: hidden`; what is painted is a <video> during content playback
// and an IMA iframe only during an ad break. Probing iframes alone reads 0 for
// most of the player's on-screen life.
//
// The band is a chain, not one unit. Units resting on the viewport bottom seed
// it, and any unit whose bottom sits on the band's current top edge extends it.
// With mobile adhesion on, Journey stacks the player directly above the anchor,
// so the player's bottom is the anchor's top rather than the viewport edge.

const CONTRIBUTION_KEY = "ad-anchor"
// Below this a creative is a tracking pixel or a collapsed unfilled slot.
const MIN_ANCHOR_HEIGHT = 30
const MIN_ANCHOR_WIDTH = 100
// The Universal Player runs 100-128px and standard adhesion 50-100px, so a
// stacked player plus anchor tops out near 230. The cap only guards against a
// full-screen interstitial pushing every FAB off-screen; the dock hook clamps
// the total again at 280.
const MAX_ANCHOR_HEIGHT = 250
// How close a unit's bottom must be to the viewport bottom, or to the top of
// the band below it, to count as pinned. Only units resting on the bottom edge
// or on another pinned unit displace our bars — the desktop Universal Player
// floats clear of it (bottom 795 in a 900px viewport) and must not count, or
// every first-party bar would jump 233px.
const BOTTOM_EDGE_TOLERANCE = 8
// Depth to search for a fixed ancestor. Journey nests three deep today; the
// extra levels absorb another wrapper without a code change. Bounded so an
// in-content iframe that merely scrolls to the bottom edge never qualifies.
const MAX_FIXED_ANCESTOR_DEPTH = 6

function hasFixedAncestor(el: Element): boolean {
  let node: Element | null = el.parentElement
  for (let depth = 0; node && node !== document.body && depth < MAX_FIXED_ANCESTOR_DEPTH; depth++) {
    if (window.getComputedStyle(node).position === "fixed") return true
    node = node.parentElement
  }
  return false
}

type Creative = { top: number; bottom: number }

function collectPinnedCreatives(): Creative[] {
  const creatives: Creative[] = []

  for (const el of Array.from(document.querySelectorAll("iframe, video"))) {
    const style = window.getComputedStyle(el)
    if (style.display === "none" || style.visibility === "hidden") continue

    const rect = el.getBoundingClientRect()
    if (rect.height < MIN_ANCHOR_HEIGHT || rect.height > MAX_ANCHOR_HEIGHT) continue
    if (rect.width < MIN_ANCHOR_WIDTH) continue
    if (!hasFixedAncestor(el)) continue

    creatives.push({ top: rect.top, bottom: rect.bottom })
  }

  return creatives
}

function measureAnchorHeight(): number {
  const creatives = collectPinnedCreatives()
  let edge = window.innerHeight

  // Grow the band upward from the viewport bottom: each pass admits every
  // creative resting on the current top edge and raises the edge to the
  // highest of them. A creative floating clear of the band never joins it.
  for (let grew = true; grew; ) {
    grew = false
    for (const c of creatives) {
      if (c.top >= edge || edge - c.bottom > BOTTOM_EDGE_TOLERANCE) continue
      edge = c.top
      grew = true
    }
  }

  return Math.min(window.innerHeight - edge, MAX_ANCHOR_HEIGHT)
}

export function AnchorAdDockSync() {
  useEffect(() => {
    let last = -1
    const sync = () => {
      const height = measureAnchorHeight()
      if (height !== last) {
        last = height
        setBottomDockContribution(CONTRIBUTION_KEY, height)
      }
    }

    // Ad wrappers append their containers directly to <body>; childList-only
    // observation (no subtree) keeps this near-free while catching insertion.
    const observer = new MutationObserver(sync)
    observer.observe(document.body, { childList: true })

    // The anchor can also resize, fill late, or be closed by the user via its
    // own close button — none of which mutate body's child list. A slow poll
    // covers those; measureAnchorHeight is a handful of getComputedStyle calls.
    const interval = window.setInterval(sync, 2000)
    sync()

    return () => {
      observer.disconnect()
      window.clearInterval(interval)
      setBottomDockContribution(CONTRIBUTION_KEY, 0)
    }
  }, [])

  return null
}
